(() => {
  const toneMap = {
    'kitchen-soft-white': {
      light: '#f6f1e8',
      mid: '#e9dfcf',
      dark: '#d8cab5'
    },
    'bathroom-warm-greige': {
      light: '#ded4c7',
      mid: '#c5baac',
      dark: '#aba08f'
    },
    'wet-area-concrete': {
      light: '#c9c9c7',
      mid: '#aeafac',
      dark: '#8f908b'
    },
    'sanitary-clean-white': {
      light: '#f7f7f4',
      mid: '#ecece8',
      dark: '#dbdbd6'
    },
    'sanitary-pure-white': {
      light: '#ffffff',
      mid: '#f5f5f3',
      dark: '#ececea'
    },
    'sanitary-cool-white': {
      light: '#f8fafb',
      mid: '#edf1f3',
      dark: '#e0e6ea'
    },
    'bathroom-soft-ivory': {
      light: '#f8f3ea',
      mid: '#efe7d9',
      dark: '#ddd1bf'
    },
    'neutral-limestone-white': {
      light: '#f4f3ef',
      mid: '#ebe9e2',
      dark: '#ddd8ce'
    },
    'laundry-charcoal': {
      light: '#7c7a77',
      mid: '#61605d',
      dark: '#474644'
    },
    'outdoor-sandstone': {
      light: '#ead9be',
      mid: '#d6bf9f',
      dark: '#b49a79'
    }
  };

  const DEFAULT_GROUT_CM = 0.6;
  const GROUT_CM_MIN = 0.2;
  const GROUT_CM_MAX = 1.5;
  const CM_TO_RENDER_PX = 10;
  const MIN_CANVAS_SIZE = 40;

  const updateUrl = (colour, tone, grout) => {
    const url = new URL(window.location.href);

    if (colour) {
      url.searchParams.set('vc', colour);
    } else {
      url.searchParams.delete('vc');
    }

    if (tone && tone !== 'kitchen-soft-white') {
      url.searchParams.set('vt', tone);
    } else {
      url.searchParams.delete('vt');
    }

    if (grout && grout !== `${DEFAULT_GROUT_CM}`) {
      url.searchParams.set('vg', grout);
    } else {
      url.searchParams.delete('vg');
    }

    window.history.replaceState({}, '', url.toString());
  };

  const sanitizeColour = (value, fallback) => {
    if (!value) return fallback;
    const trimmed = value.trim();
    const isHex = /^#([0-9a-fA-F]{3,8})$/.test(trimmed);
    const isRgb = /^rgba?\([\d\s,%.]+\)$/.test(trimmed);
    const isHsl = /^hsla?\([\d\s,%.]+\)$/.test(trimmed);
    return isHex || isRgb || isHsl ? trimmed : fallback;
  };

  const normalizeFinish = (value) => {
    const finish = (value || '').toLowerCase();
    if (finish.includes('matt') || finish.includes('matte')) return 'matt';
    if (finish.includes('satin')) return 'satin';
    if (finish.includes('gloss') || finish.includes('glossy')) return 'gloss';
    return 'standard';
  };

  const buildFinishOverlay = (finishType, width, height) => {
    if (finishType === 'matt') {
      return {
        defs: '',
        overlay: `<rect width="${width}" height="${height}" fill="#000" opacity="0.03" />`
      };
    }
    if (finishType === 'satin') {
      return {
        defs: `
          <linearGradient id="finish-glow" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="#fff" stop-opacity="0.18" />
            <stop offset="55%" stop-color="#fff" stop-opacity="0" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.08" />
          </linearGradient>
        `,
        overlay: `<rect width="${width}" height="${height}" fill="url(#finish-glow)" />`
      };
    }
    if (finishType === 'gloss') {
      return {
        defs: `
          <linearGradient id="finish-gloss" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="#fff" stop-opacity="0.32" />
            <stop offset="40%" stop-color="#fff" stop-opacity="0.08" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.1" />
          </linearGradient>
        `,
        overlay: `
          <rect width="${width}" height="${height}" fill="url(#finish-gloss)" />
          <ellipse cx="${Math.round(width * 0.23)}" cy="${Math.round(height * 0.24)}" rx="${Math.round(width * 0.27)}" ry="${Math.round(height * 0.15)}" fill="#fff" opacity="0.18" />
        `
      };
    }
    return {
      defs: '',
      overlay: `<rect width="${width}" height="${height}" fill="#fff" opacity="0.04" />`
    };
  };

  const buildEmbossFilter = (finishType) => {
    let shadowOpacity = 0.2;
    let highlightOpacity = 0.18;
    let blur = 0.7;

    if (finishType === 'matt') {
      shadowOpacity = 0.14;
      highlightOpacity = 0.1;
      blur = 0.9;
    } else if (finishType === 'gloss') {
      shadowOpacity = 0.24;
      highlightOpacity = 0.24;
      blur = 0.6;
    }

    return `
      <filter id="tile-emboss" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="${blur}" result="tile-alpha-blur" />
        <feOffset in="tile-alpha-blur" dx="0.9" dy="0.9" result="tile-shadow-offset" />
        <feOffset in="tile-alpha-blur" dx="-0.9" dy="-0.9" result="tile-highlight-offset" />
        <feFlood flood-color="#000" flood-opacity="${shadowOpacity}" result="tile-shadow-flood" />
        <feComposite in="tile-shadow-flood" in2="tile-shadow-offset" operator="in" result="tile-shadow" />
        <feFlood flood-color="#fff" flood-opacity="${highlightOpacity}" result="tile-highlight-flood" />
        <feComposite in="tile-highlight-flood" in2="tile-highlight-offset" operator="in" result="tile-highlight" />
        <feTurbulence type="fractalNoise" baseFrequency="0.78" numOctaves="2" seed="7" result="tile-noise" />
        <feColorMatrix in="tile-noise" type="saturate" values="0" result="tile-noise-gray" />
        <feComponentTransfer in="tile-noise-gray" result="tile-noise-soft">
          <feFuncA type="linear" slope="0.06" />
        </feComponentTransfer>
        <feBlend in="SourceGraphic" in2="tile-noise-soft" mode="soft-light" result="tile-ceramic" />
        <feMerge>
          <feMergeNode in="tile-shadow" />
          <feMergeNode in="tile-ceramic" />
          <feMergeNode in="tile-highlight" />
        </feMerge>
      </filter>
    `;
  };

  const computeTileFit = (width, height, grout) => {
    const area = width * height;
    const targetTile = Math.max(42, Math.min(98, Math.sqrt(area / 36)));
    const cols = Math.max(3, Math.round((width + grout) / (targetTile + grout)));
    const rows = Math.max(3, Math.round((height + grout) / (targetTile + grout)));
    const tileW = Math.max(12, (width - grout * (cols + 1)) / cols);
    const tileH = Math.max(12, (height - grout * (rows + 1)) / rows);

    return { cols, rows, tileW, tileH };
  };

  const tileSceneSvg = (layout, palette, groutWidthCm, jointColor, finish, sceneWidth, sceneHeight) => {
    const width = Math.max(240, Number.parseInt(sceneWidth, 10) || 480);
    const height = Math.max(160, Number.parseInt(sceneHeight, 10) || 320);
    const groutCm = Number.parseFloat(groutWidthCm);
    const grout = Math.max(2, Math.min(16, (Number.isNaN(groutCm) ? DEFAULT_GROUT_CM : groutCm) * CM_TO_RENDER_PX));
    const tileLight = sanitizeColour(palette.light, '#f4efe7');
    const tileMid = sanitizeColour(palette.mid, '#ddd3c3');
    const tileDark = sanitizeColour(palette.dark, '#c4b7a2');
    const joint = sanitizeColour(jointColor, '#d9d9d9');
    const finishType = normalizeFinish(finish);
    const finishOverlay = buildFinishOverlay(finishType, width, height);
    const embossFilter = buildEmbossFilter(finishType);
    const edgeInset = Math.max(grout + 2, 8);
    const drawableWidth = Math.max(MIN_CANVAS_SIZE, width - edgeInset * 2);
    const drawableHeight = Math.max(MIN_CANVAS_SIZE, height - edgeInset * 2);

    const rects = [];
    const pushRect = (x, y, w, h, fill) => {
      rects.push(
        `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${fill}" stroke="${fill}" stroke-width="1" filter="url(#tile-emboss)" />`
      );
    };

    const fit = computeTileFit(drawableWidth, drawableHeight, grout);
    const tileW = fit.tileW;
    const tileH = fit.tileH;
    const uniformTileFill = finishType === 'gloss' ? tileLight : tileMid;
    for (let row = 0; row < fit.rows; row += 1) {
      const y = edgeInset + row * (tileH + grout);
      for (let col = 0; col < fit.cols; col += 1) {
        const x = edgeInset + col * (tileW + grout);
        pushRect(x, y, tileW, tileH, uniformTileFill);
      }
    }

    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet">
  <defs>
    ${embossFilter}
    ${finishOverlay.defs}
  </defs>
  <rect width="${width}" height="${height}" fill="${joint}" />
  ${rects.join('')}
  ${finishOverlay.overlay}
</svg>`;
    return svg;
  };

  class SealantVisualiser {
    constructor(root) {
      this.root = root;
      this.colourSelect = root.querySelector('[data-visualiser-colour-select]');
      this.toneChips = Array.from(root.querySelectorAll('[data-tone-chip]'));
      this.groutRange = root.querySelector('[data-visualiser-grout-range]');
      this.groutOutput = root.querySelector('[data-visualiser-grout-output]');
      this.groutPresets = Array.from(root.querySelectorAll('[data-grout-preset]'));
      this.colourSearch = root.querySelector('[data-visualiser-colour-search]');
      this.colourChips = Array.from(root.querySelectorAll('[data-colour-chip]'));
      this.shopCta = root.querySelector('[data-visualiser-shop-cta]');
      this.sampleCta = root.querySelector('[data-visualiser-sample-cta]');
      this.status = root.querySelector('[data-visualiser-status]');
      this.sceneCanvases = Array.from(root.querySelectorAll('.sealant-scene__canvas[data-layout]'));
      this.activeToneKey = 'kitchen-soft-white';
      this.currentRenderState = null;
      this.resizeRaf = null;
      this.renderRaf = null;
      this.canvasResizeObserver = null;
      this.handleResize = this.handleResize.bind(this);
      this.handleCanvasResize = this.handleCanvasResize.bind(this);

      if (!this.colourSelect || !this.groutRange) return;

      this.applyInitialStateFromUrl();
      this.bindEvents();
      this.applyState({ syncUrl: false });
    }

    bindEvents() {
      this.colourSelect.addEventListener('change', () => this.applyState());
      this.groutRange.addEventListener('input', () => this.applyState());
      if (this.colourSearch) {
        this.colourSearch.addEventListener('input', () => this.filterColourChips());
      }

      this.groutPresets.forEach((preset) => {
        preset.addEventListener('click', () => {
          const presetValue = preset.dataset.groutValue || `${DEFAULT_GROUT_CM}`;
          this.groutRange.value = presetValue;
          this.applyState();
        });
      });

      this.toneChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const toneKey = chip.dataset.toneKey || 'kitchen-soft-white';
          if (!toneMap[toneKey]) return;
          this.activeToneKey = toneKey;
          this.applyState();
        });
      });

      this.colourChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const index = Number.parseInt(chip.dataset.chipIndex || '-1', 10);
          if (Number.isNaN(index) || index < 0) return;
          this.colourSelect.selectedIndex = index;
          this.applyState();
        });
      });

      window.addEventListener('resize', this.handleResize);

      if ('ResizeObserver' in window) {
        this.canvasResizeObserver = new window.ResizeObserver(this.handleCanvasResize);
        this.sceneCanvases.forEach((canvas) => {
          this.canvasResizeObserver.observe(canvas);
        });
      }
    }

    applyInitialStateFromUrl() {
      const url = new URL(window.location.href);
      const colourParam = (url.searchParams.get('vc') || '').trim().toLowerCase();
      let toneParam = (url.searchParams.get('vt') || '').trim().toLowerCase();
      const groutParam = (url.searchParams.get('vg') || '').trim();

      if (toneParam === 'light' || toneParam === 'coastal-white') toneParam = 'kitchen-soft-white';
      if (toneParam === 'neutral' || toneParam === 'warm-greige') toneParam = 'bathroom-warm-greige';
      if (toneParam === 'dark' || toneParam === 'concrete') toneParam = 'wet-area-concrete';
      if (toneParam === 'charcoal') toneParam = 'laundry-charcoal';
      if (toneParam === 'sandstone') toneParam = 'outdoor-sandstone';
      if (toneParam === 'sanitary-white') toneParam = 'sanitary-clean-white';

      if (colourParam) {
        const options = Array.from(this.colourSelect.options);
        const matchedIndex = options.findIndex((option) => option.value.trim().toLowerCase() === colourParam);
        if (matchedIndex >= 0) {
          this.colourSelect.selectedIndex = matchedIndex;
        }
      }

      if (toneMap[toneParam]) {
        this.activeToneKey = toneParam;
      }

      const groutValue = Number.parseFloat(groutParam);
      if (!Number.isNaN(groutValue)) {
        const clamped = Math.max(GROUT_CM_MIN, Math.min(GROUT_CM_MAX, groutValue));
        this.groutRange.value = clamped.toFixed(1);
      }
    }

    applyState({ syncUrl = true } = {}) {
      const selectedOption = this.colourSelect.options[this.colourSelect.selectedIndex];
      if (!selectedOption) return;

      const toneKey = this.activeToneKey in toneMap ? this.activeToneKey : 'kitchen-soft-white';
      const tone = toneMap[toneKey];
      const groutCm = this.groutRange.value || `${DEFAULT_GROUT_CM}`;
      const groutCmNumeric = Number.parseFloat(groutCm);
      const renderPx = (Number.isNaN(groutCmNumeric) ? DEFAULT_GROUT_CM : groutCmNumeric) * CM_TO_RENDER_PX;

      const colourName = selectedOption.dataset.colourName || selectedOption.value;
      const colourHex = selectedOption.dataset.colourHex || '#d9d9d9';
      const colourRal = selectedOption.dataset.colourRal || '';
      const colourFinish = selectedOption.dataset.colourFinish || '';
      const primaryTitle = selectedOption.dataset.primaryTitle || '';
      const primaryUrl = selectedOption.dataset.primaryUrl || '';

      this.root.style.setProperty('--visualiser-sealant-colour', colourHex);
      this.root.style.setProperty('--visualiser-tile-light', tone.light);
      this.root.style.setProperty('--visualiser-tile-mid', tone.mid);
      this.root.style.setProperty('--visualiser-tile-dark', tone.dark);
      this.root.style.setProperty('--visualiser-grout-width', `${renderPx}px`);

      if (this.groutOutput) {
        this.groutOutput.value = `${groutCm}cm`;
        this.groutOutput.textContent = `${groutCm} cm`;
      }

      this.colourChips.forEach((chip, chipIndex) => {
        chip.classList.toggle('is-active', chipIndex === this.colourSelect.selectedIndex);
      });
      this.filterColourChips();
      this.scrollActiveChipIntoView();
      this.syncGroutPresetState(groutCm);
      this.toneChips.forEach((chip) => {
        chip.classList.toggle('is-active', (chip.dataset.toneKey || '') === toneKey);
      });

      this.updateShopCta(primaryUrl, primaryTitle, colourName);
      this.updateSampleCta(colourName);
      this.updateStatus(colourName, colourHex, colourRal, colourFinish);
      this.currentRenderState = {
        tone,
        groutCm,
        colourHex,
        colourFinish
      };
      this.scheduleSceneRender();
      if (syncUrl) {
        updateUrl(colourName, toneKey, groutCm);
      }
    }

    syncGroutPresetState(groutCm) {
      const normalized = Number.parseFloat(groutCm).toFixed(1);
      this.groutPresets.forEach((preset) => {
        const presetValue = Number.parseFloat(preset.dataset.groutValue || `${DEFAULT_GROUT_CM}`).toFixed(1);
        preset.classList.toggle('is-active', presetValue === normalized);
      });
    }

    filterColourChips() {
      if (!this.colourSearch) return;
      const query = this.colourSearch.value.trim().toLowerCase();
      this.colourChips.forEach((chip, chipIndex) => {
        const selected = chipIndex === this.colourSelect.selectedIndex;
        const name = (chip.dataset.colourChipName || '').toLowerCase();
        const matches = !query || name.includes(query);
        chip.hidden = !selected && !matches;
      });
    }

    scrollActiveChipIntoView() {
      const activeChip = this.colourChips[this.colourSelect.selectedIndex];
      if (!activeChip || typeof activeChip.scrollIntoView !== 'function') return;

      activeChip.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    scheduleSceneRender() {
      if (!this.currentRenderState) return;
      if (this.renderRaf) {
        window.cancelAnimationFrame(this.renderRaf);
      }

      this.renderRaf = window.requestAnimationFrame(() => {
        this.renderRaf = window.requestAnimationFrame(() => {
          if (!this.currentRenderState) return;
          this.renderScenes(
            this.currentRenderState.tone,
            this.currentRenderState.groutCm,
            this.currentRenderState.colourHex,
            this.currentRenderState.colourFinish
          );
        });
      });
    }

    renderScenes(tone, grout, colourHex, colourFinish) {
      if (!this.sceneCanvases.length) return;
      this.sceneCanvases.forEach((canvas) => {
        const layout = canvas.dataset.layout || 'grid';
        const bounds = canvas.getBoundingClientRect();
        const measuredWidth = Math.round(canvas.clientWidth || bounds.width || 0);
        const measuredHeight = Math.round(canvas.clientHeight || bounds.height || 0);
        const canvasWidth = measuredWidth >= MIN_CANVAS_SIZE ? measuredWidth : 480;
        const canvasHeight = measuredHeight >= MIN_CANVAS_SIZE ? measuredHeight : 320;
        canvas.innerHTML = tileSceneSvg(layout, tone, grout, colourHex, colourFinish, canvasWidth, canvasHeight);
      });
    }

    handleResize() {
      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
      }
      this.resizeRaf = window.requestAnimationFrame(() => {
        this.scheduleSceneRender();
      });
    }

    handleCanvasResize() {
      this.scheduleSceneRender();
    }

    updateShopCta(primaryUrl, primaryTitle, colourName) {
      if (!this.shopCta) return;

      if (primaryUrl) {
        this.shopCta.href = primaryUrl;
        this.shopCta.classList.remove('is-disabled');
        this.shopCta.setAttribute('aria-disabled', 'false');
        this.shopCta.title = primaryTitle ? `Shop ${primaryTitle}` : `Shop ${colourName}`;
      } else {
        this.shopCta.href = '#';
        this.shopCta.classList.add('is-disabled');
        this.shopCta.setAttribute('aria-disabled', 'true');
        this.shopCta.title = `No linked product for ${colourName} yet`;
      }
    }

    updateSampleCta(colourName) {
      if (!this.sampleCta) return;
      const baseUrl = this.sampleCta.dataset.sampleBaseUrl || '';
      if (!baseUrl) return;

      try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('colour', colourName);
        this.sampleCta.href = url.pathname + url.search + url.hash;
      } catch (_error) {
        this.sampleCta.href = baseUrl;
      }
    }

    updateStatus(colourName, colourHex, colourRal, colourFinish) {
      if (!this.status) return;
      this.status.textContent = `Previewing ${colourName} ${colourHex}${colourRal ? `, RAL ${colourRal}` : ''}${colourFinish ? `, ${colourFinish} finish` : ''}.`;
    }
  }

  const init = () => {
    document.querySelectorAll('[data-sealant-visualiser]').forEach((root) => {
      if (!root.__sealantVisualiser) {
        root.__sealantVisualiser = new SealantVisualiser(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
